/*
  File: SchedulePage.tsx
  Path: /Users/larrymclean/CascadeProjects/aquorix-frontend/src/features/dashboard/pages/SchedulePage.tsx
  Description:
    Phase 5A: Render operator-scoped weekly schedule from backend API.
    Phase 5B: Create test session (mutation) + deterministic refresh.
    Phase 5C: Cancel session (mutation) + deterministic refresh.
    Phase 6: Week navigation (prev/next 7 days) with backend-anchored floor clamp.

    Key behaviors:
    - UI does not blank during refresh
    - Create disables only while creating
    - Cancel disables only the selected session while cancelling
    - Errors are surfaced deterministically (403/409 banners remain)
    - Week nav is backend-authoritative:
        - Initial weekStart comes from backend week.start
        - weekFloor locks to that initial backend week.start
        - Prev clamps to weekFloor (never earlier)
        - Next always advances +7 days

  Author: AQUORIX Team
  Created: 2026-02-14
  Version: 1.3.6

  Change Log:
    - 2026-02-14 - v1.0.0:
      - Phase 5A initial wiring: GET /api/v1/dashboard/schedule and render grouped list
    - 2026-02-15 - v1.2.0:
      - Keep button visible during refresh (no flash) + refresh() hook
    - 2026-02-15 - v1.3.0:
      - Phase 5C: Add cancel button per session row
      - Cancel calls cancelDashboardSession() then refresh()
    - 2026-02-17 - v1.3.1:
      - Phase 6: Replaced hard coded test dates with real date/time system variable code
    - 2026-02-17 - v1.3.2:
      - Phase 6: Modify weekStart date
    - 2026-02-17 - v1.3.3:
      - Phase 6: Add weekFloor anchored to backend week.start
      - Phase 6: Prev clamps to weekFloor (not browser today) and disables at floor
    - 2026-05-14 - v1.3.4:
      - Wire Operational Schedule rows to shared DashboardSessionCard presentation component.
      - Preserve existing Cancel/Delete behavior through card action slot.
    - 2026-05-15 - v1.3.5:
      - Add visible Edit action to dive session cards as first step toward schedule edit workflow.
    - 2026-05-31 - v1.3.6:
      - Create Dive Session now defaults to current local date instead of backend week start.
      - Add minimum date guard to prevent accidental past-date session creation.
*/

import React, { useEffect, useMemo, useState } from "react";
import { useDashboardSchedule } from "../../../hooks/useDashboardSchedule";
import type { DashboardScheduleSession } from "../../../types/dashboardSchedule";
import "./SchedulePage.css";
import {
  createDashboardSession,
  updateDashboardSession,
  cancelDashboardSession,
  deleteDashboardSession,
  updateDashboardCourseRun,
  cancelDashboardCourseRun,
  getDashboardSchedulePatterns,
  createDashboardSchedulePattern,
  updateDashboardSchedulePattern,
  deleteDashboardSchedulePattern,
  generateDashboardScheduleFromPatterns,
  getDashboardSchedulePatternLookups
} from "../../../api/dashboardSchedule";

function shortTime(value: string | null | undefined) {
  if (!value) return "—";
  return String(value).slice(0, 5);
}

function weekdayFromYmd(value: string) {
  const date = new Date(`${value}T12:00:00`);
  return date.toLocaleDateString("en-US", { weekday: "long" });
}

const TEMPLATE_TIME_OPTIONS = [
  "06:00", "06:30",
  "07:00", "07:30",
  "08:00", "08:30",
  "09:00", "09:30",
  "10:00", "10:30",
  "11:00", "11:30",
  "12:00", "12:30",
  "13:00", "13:30",
  "14:00", "14:30",
  "15:00", "15:30",
  "16:00", "16:30",
  "17:00", "17:30",
  "18:00", "18:30",
  "19:00", "19:30",
  "20:00", "20:30",
  "21:00"
];

function currentLocalYmd() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function subtractThirtyMinutes(time: string) {
  const [hoursRaw, minutesRaw] = time.split(":");
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return time;

  const total = Math.max(0, hours * 60 + minutes - 30);
  const newHours = Math.floor(total / 60);
  const newMinutes = total % 60;

  return `${String(newHours).padStart(2, "0")}:${String(newMinutes).padStart(2, "0")}`;
}

function sessionTypeLabel(value: string | null | undefined) {
  if (value === "shore") return "Shore dive";
  if (value === "boat") return "Boat dive";
  return value || "Type TBD";
}

function groupByDate(sessions: DashboardScheduleSession[]) {
  const map: Record<string, DashboardScheduleSession[]> = {};
  for (const s of sessions) {
    const key = s.session_date || "unknown-date";
    if (!map[key]) map[key] = [];
    map[key].push(s);
  }

  for (const k of Object.keys(map)) {
    map[k].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  }

  const dates = Object.keys(map).sort((a, b) => a.localeCompare(b));
  return { dates, map };
}

const RHYTHM_DAYS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 7, label: "Sun" }
];

function groupPatternsByWeekday(patterns: any[]) {
  const map: Record<number, any[]> = {};

  for (const day of RHYTHM_DAYS) {
    map[day.value] = [];
  }

  for (const pattern of patterns) {
    const weekday = Number(pattern.weekday || 0);
    if (!map[weekday]) continue;
    map[weekday].push(pattern);
  }

  for (const day of RHYTHM_DAYS) {
    map[day.value].sort((a, b) => shortTime(a.start_time).localeCompare(shortTime(b.start_time)));
  }

  return map;
}

function rhythmChipClass(pattern: any) {
  if (String(pattern.dive_category || "").toLowerCase().includes("course")) return "tchip course";
  if (String(pattern.session_type || "").toLowerCase() === "boat") return "tchip boat";
  return "tchip";
}

function rhythmEntryLabel(pattern: any) {
  if (String(pattern.session_type || "").toLowerCase() === "boat") return "Boat";
  return "Shore";
}

function rhythmPatternPrice(pattern: any) {
  if (!pattern.price_per_diver) return "Price TBD";
  return `${Number(pattern.price_per_diver).toFixed(0)} JOD`;
}

function monthDay(value: string) {
  const d = new Date(`${value}T12:00:00`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function addDaysYmd(value: string, days: number) {
  const d = new Date(`${value}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

function groupBySlot(sessions: DashboardScheduleSession[]) {
  const map: Record<string, DashboardScheduleSession[]> = {};

  for (const s of sessions) {
    const key = s.start_time || "TBD";
    if (!map[key]) map[key] = [];
    map[key].push(s);
  }

  return Object.keys(map).sort((a, b) => a.localeCompare(b)).map((time) => ({
    time,
    sessions: map[time]
  }));
}

function capacityUsed(s: any) {
  return Number(s.seats_used ?? s.capacity_consumed ?? 0);
}

function capacityTotal(s: any) {
  return Number(s.capacity_total ?? 0);
}

function dayFill(sessions: DashboardScheduleSession[]) {
  const used = sessions.reduce((sum, s: any) => sum + capacityUsed(s), 0);
  const total = sessions.reduce((sum, s: any) => sum + capacityTotal(s), 0);
  return `${used} / ${total} seats`;
}

function vesselCount(sessions: DashboardScheduleSession[]) {
  return new Set(
    sessions
      .map((s: any) => s.vessel_name || s.metadata?.vessel_name)
      .filter(Boolean)
  ).size;
}

function sessionClassName(s: any) {
  if (s.type === "course") return "sess course";
  if (s.session_type === "boat" || s.metadata?.session_type === "boat") return "sess boat";
  return "sess";
}

function sessionKindLabel(s: any) {
  if (s.type === "course") return "Course";
  if (s.session_type === "boat" || s.metadata?.session_type === "boat") return "Boat";
  return "Shore";
}

function entryLabel(s: any) {
  if (s.type === "course") return s.metadata?.certification_agency || "Course run";
  return sessionTypeLabel(s.session_type || s.metadata?.session_type);
}

function vesselLabel(s: any) {
  return s.vessel_name || s.metadata?.vessel_name || "No vessel";
}

function guideLabel(s: any) {
  return s.guide_name || s.metadata?.lead_guide_name || s.metadata?.instructor_name || null;
}

function meetLocationLabel(s: any) {
  return s.meet_location || s.metadata?.meet_location || s.metadata?.location || "Location TBD";
}

function priceParts(amount: number | null | undefined, currency: string | null | undefined) {
  if (amount === null || amount === undefined || !currency) return ["Price TBD", ""];
  const divisor = currency === "JOD" ? 1000 : 100;
  return [(amount / divisor).toFixed(2), currency];
}

function capPercent(s: any) {
  const total = capacityTotal(s);
  if (!total) return 0;
  return Math.min(Math.round((capacityUsed(s) / total) * 100), 100);
}

function capClass(s: any) {
  const pct = capPercent(s);
  if (pct >= 100) return "full";
  if (pct >= 60) return "mid";
  return "";
}

function ErrorBanner({ error }: { error: any }) {
  const status = error?.status;
  const message = error?.message || "Unknown error";

  if (status === 403) {
    return (
      <div style={styles.bannerWarn}>
        <strong>403 — No operator affiliation.</strong>
        <div>This user is not linked to any operator. (Data state problem)</div>
      </div>
    );
  }

  if (status === 409) {
    const count = error?.raw?.affiliation_count;
    return (
      <div style={styles.bannerWarn}>
        <strong>409 — Multiple operator affiliations.</strong>
        <div>Operator selection required (Phase 6). Current affiliations: {count ?? "unknown"}.</div>
        <div style={{ marginTop: 6, opacity: 0.85 }}>
          Fix for now: ensure exactly ONE active row in <code>aquorix.user_operator_affiliations</code>.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.bannerError}>
      <strong>Schedule load failed.</strong>
      <div>{message}</div>
    </div>
  );
}

export default function SchedulePage() {
  const [weekStart, setWeekStart] = useState<string | null>(null);
  const [weekFloor, setWeekFloor] = useState<string | null>(null);

  const { state, refresh } = useDashboardSchedule(weekStart || undefined);

  const [isCreating, setIsCreating] = useState(false);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [patterns, setPatterns] = useState<any[]>([]);
  const [patternsLoading, setPatternsLoading] = useState(false);
  const [patternLookups, setPatternLookups] = useState<any>({
    dive_sites: [],
    vessels: [],
    guides: [],
    meet_locations: [],
    dive_categories: []
  });
  const [isCreatingPattern, setIsCreatingPattern] = useState(false);
  const [isGeneratingFromPatterns, setIsGeneratingFromPatterns] = useState(false);
  const [editingPatternId, setEditingPatternId] = useState<string | null>(null);
  const [applyTargetWeek, setApplyTargetWeek] = useState<string>("");
  const [applyDialog, setApplyDialog] = useState<any>({ open: false, result: null });
  const [rhythmForm, setRhythmForm] = useState({
    weekday: "1",
    start_time: "08:30",
    meet_time: "08:00",
    meet_location: "",
    dive_site_id: "",
    session_type: "shore",
    dive_category: "",
    vessel_id: "",
    lead_guide_id: "",
    default_capacity: "10",
    price_per_diver: "45.000"
  });
  const [scheduleView, setScheduleView] = useState<"schedule" | "template">("schedule");
  const [showCreateSessionForm, setShowCreateSessionForm] = useState(false);
  const [collapsedDays, setCollapsedDays] = useState<Record<string, boolean>>({});

  const [sessionForm, setSessionForm] = useState({
    session_date: currentLocalYmd(),
    start_time: "08:30",
    meet_time: "08:00",
    meet_location: "",
    dive_site_id: "",
    session_type: "shore",
    dive_category: "",
    vessel_id: "",
    lead_guide_id: "",
    price_per_diver: "45.000"
  });

  const [editingSession, setEditingSession] = useState<any | null>(null);
  const [isUpdatingSession, setIsUpdatingSession] = useState(false);
  const [editSessionForm, setEditSessionForm] = useState({
    session_date: currentLocalYmd(),
    start_time: "08:30",
    meet_time: "08:00",
    dive_site_id: "",
    session_type: "shore",
    vessel_id: "",
    lead_guide_id: "",
    price_per_diver: "45.000"
  });

  const [editingCourseRun, setEditingCourseRun] = useState<any | null>(null);
  const [isUpdatingCourseRun, setIsUpdatingCourseRun] = useState(false);
  const [editCourseRunForm, setEditCourseRunForm] = useState({
    session_date: currentLocalYmd(),
    start_time: "09:00",
    instructor_id: "",
    location: "",
    max_capacity: "8"
  });
  const [pendingCourseCancel, setPendingCourseCancel] = useState<any | null>(null);
  const [isCancellingCourseRun, setIsCancellingCourseRun] = useState(false);
  const [courseCancelError, setCourseCancelError] = useState<string | null>(null);

  const scheduleData = (state as any).data as any;

  useEffect(() => {
    const backendStart: string | undefined = scheduleData?.week?.start;
    if (!backendStart) return;

    if (!weekStart) setWeekStart(backendStart);
    if (!weekFloor) setWeekFloor(backendStart);
    if (!applyTargetWeek) setApplyTargetWeek(backendStart);
  }, [scheduleData?.week?.start, weekStart, weekFloor, applyTargetWeek]);

  const grouped = useMemo(() => {
    if (!scheduleData?.sessions) return { dates: [], map: {} as Record<string, DashboardScheduleSession[]> };
    return groupByDate(scheduleData.sessions);
  }, [scheduleData]);

  const groupedDatesKey = grouped.dates.join("|");
  const todayYmd = currentLocalYmd();

  const currentDates = useMemo(() => {
    return grouped.dates.filter((date) => date >= todayYmd);
  }, [groupedDatesKey, grouped.dates, todayYmd]);

  const pastDates = useMemo(() => {
    return grouped.dates.filter((date) => date < todayYmd);
  }, [groupedDatesKey, grouped.dates, todayYmd]);

  const visibleDates = useMemo(() => {
    return [...currentDates, ...pastDates];
  }, [currentDates, pastDates]);

  const visibleDatesKey = visibleDates.join("|");

  useEffect(() => {
    const nextState: Record<string, boolean> = {};

    currentDates.forEach((date) => {
      nextState[date] = false;
    });

    pastDates.forEach((date) => {
      nextState[date] = true;
    });

    setCollapsedDays(nextState);
  }, [visibleDatesKey, currentDates, pastDates]);

  async function refreshPatterns() {
    setPatternsLoading(true);

    try {
      const json = await getDashboardSchedulePatterns();
      setPatterns(Array.isArray(json.patterns) ? json.patterns : []);
    } catch (err: any) {
      alert(err?.message || "Load weekly templates failed");
    } finally {
      setPatternsLoading(false);
    }
  }

  useEffect(() => {
    async function loadScheduleTemplateData() {
      await refreshPatterns();

      try {
        const json = await getDashboardSchedulePatternLookups();
        setPatternLookups({
          dive_sites: Array.isArray(json.dive_sites) ? json.dive_sites : [],
          vessels: Array.isArray(json.vessels) ? json.vessels : [],
          guides: Array.isArray(json.guides) ? json.guides : [],
          meet_locations: Array.isArray(json.meet_locations) ? json.meet_locations : [],
          dive_categories: Array.isArray(json.dive_categories) ? json.dive_categories : []
        });
      } catch (err: any) {
        alert(err?.message || "Load schedule template lookups failed");
      }
    }

    void loadScheduleTemplateData();
  }, []);

  function openRhythmAdd(weekday?: number) {
    setRhythmForm({
      weekday: String(weekday || 1),
      start_time: "08:30",
      meet_time: "08:00",
      meet_location: "",
      dive_site_id: "",
      session_type: "shore",
      dive_category: "",
      vessel_id: "",
      lead_guide_id: "",
      default_capacity: "10",
      price_per_diver: "45.000"
    });
    setEditingPatternId("__new__");
  }

  function openRhythmEdit(pattern: any) {
    setRhythmForm({
      weekday: String(pattern.weekday || 1),
      start_time: shortTime(pattern.start_time),
      meet_time: shortTime(pattern.meet_time),
      meet_location: pattern.meet_location || "",
      dive_site_id: pattern.dive_site_id ? String(pattern.dive_site_id) : "",
      session_type: pattern.session_type || "shore",
      dive_category: pattern.dive_category || "",
      vessel_id: pattern.vessel_id ? String(pattern.vessel_id) : "",
      lead_guide_id: pattern.lead_guide_id ? String(pattern.lead_guide_id) : "",
      default_capacity: pattern.default_capacity ? String(pattern.default_capacity) : "10",
      price_per_diver: pattern.price_per_diver || "45.000"
    });
    setEditingPatternId(String(pattern.pattern_id));
  }

  async function handleCreateBlueCurrentPattern() {
    if (isCreatingPattern) return;

    if (!rhythmForm.dive_site_id) {
      alert("Choose a dive site before saving rhythm.");
      return;
    }

    setIsCreatingPattern(true);

    try {
      const payload = {
        template_scope: "weekly",
        weekday: Number(rhythmForm.weekday),
        start_time: rhythmForm.start_time,
        meet_time: rhythmForm.meet_time || null,
        meet_location: rhythmForm.meet_location || null,
        dive_site_id: Number(rhythmForm.dive_site_id),
        session_type: rhythmForm.session_type,
        dive_category: rhythmForm.dive_category || null,
        vessel_id: rhythmForm.vessel_id ? Number(rhythmForm.vessel_id) : null,
        lead_guide_id: rhythmForm.lead_guide_id ? Number(rhythmForm.lead_guide_id) : null,
        default_capacity: rhythmForm.default_capacity ? Number(rhythmForm.default_capacity) : null,
        price_per_diver: rhythmForm.price_per_diver || null,
      };

      if (editingPatternId && editingPatternId !== "__new__") {
        await updateDashboardSchedulePattern(Number(editingPatternId), payload as any);
      } else {
        await createDashboardSchedulePattern(payload as any);
      }

      setEditingPatternId(null);
      await refreshPatterns();
    } catch (err: any) {
      alert(err?.message || "Save rhythm failed");
    } finally {
      setIsCreatingPattern(false);
    }
  }

  async function handleDeletePattern() {
    if (!editingPatternId || editingPatternId === "__new__") return;
    if (!window.confirm("Remove this rhythm row? This changes the blueprint only — sessions already applied to a week are not affected.")) return;

    try {
      await deleteDashboardSchedulePattern(Number(editingPatternId));
      setEditingPatternId(null);
      await refreshPatterns();
    } catch (err: any) {
      alert(err?.message || "Delete rhythm failed");
    }
  }

  function openApplyDialog() {
    const start = applyTargetWeek || weekStart || scheduleData?.week?.start;
    const end = start ? addDaysYmd(start, 6) : null;

    if (!start || !end) {
      alert("Week not loaded yet.");
      return;
    }

    setApplyDialog({
      open: true,
      result: null,
      start,
      end
    });
  }

  async function confirmApplyRhythm() {
    if (isGeneratingFromPatterns || !applyDialog?.start || !applyDialog?.end) return;

    setIsGeneratingFromPatterns(true);

    try {
      const result = await generateDashboardScheduleFromPatterns({
        start_date: applyDialog.start,
        end_date: applyDialog.end,
      });

      setApplyDialog({
        ...applyDialog,
        result
      });

      refresh();
    } catch (err: any) {
      alert(err?.message || "Generate schedule from templates failed");
    } finally {
      setIsGeneratingFromPatterns(false);
    }
  }

  function renderRhythmEditor() {
    return (
      <div className="tpop" onClick={(event) => event.stopPropagation()}>
        <div className="ph"><span className="pdot"></span> Rhythm row</div>
        <div className="pgrid">
          <div className="pfld">
            <label>Day</label>
            <select value={rhythmForm.weekday} onChange={(e) => setRhythmForm({ ...rhythmForm, weekday: e.target.value })}>
              {RHYTHM_DAYS.map((day) => <option key={day.value} value={day.value}>{day.label}</option>)}
            </select>
          </div>
          <div className="pfld">
            <label>Start</label>
            <select value={rhythmForm.start_time} onChange={(e) => setRhythmForm({ ...rhythmForm, start_time: e.target.value, meet_time: subtractThirtyMinutes(e.target.value) })}>
              {TEMPLATE_TIME_OPTIONS.map((time) => <option key={time} value={time}>{time}</option>)}
            </select>
          </div>
          <div className="pfld">
            <label>Meet</label>
            <select value={rhythmForm.meet_time} onChange={(e) => setRhythmForm({ ...rhythmForm, meet_time: e.target.value })}>
              {TEMPLATE_TIME_OPTIONS.map((time) => <option key={time} value={time}>{time}</option>)}
            </select>
          </div>
          <div className="pfld">
            <label>Entry Type</label>
            <select value={rhythmForm.session_type} onChange={(e) => setRhythmForm({ ...rhythmForm, session_type: e.target.value })}>
              <option value="shore">Shore</option>
              <option value="boat">Boat</option>
            </select>
          </div>
          <div className="pfld full">
            <label>Site</label>
            <select value={rhythmForm.dive_site_id} onChange={(e) => setRhythmForm({ ...rhythmForm, dive_site_id: e.target.value })}>
              <option value="">Choose dive site...</option>
              {patternLookups.dive_sites.map((site: any) => (
                <option key={site.dive_site_id} value={site.dive_site_id}>{site.name}</option>
              ))}
            </select>
          </div>
          <div className="pfld full">
            <label>Dive Category</label>
            <select value={rhythmForm.dive_category} onChange={(e) => setRhythmForm({ ...rhythmForm, dive_category: e.target.value })}>
              <option value="">Choose...</option>
              {(patternLookups.dive_categories || []).map((category: any) => (
                <option key={category.operator_dive_category_id || category.value} value={category.value}>{category.label}</option>
              ))}
            </select>
          </div>
          <div className="pfld">
            <label>Vehicle / Vessel</label>
            <select value={rhythmForm.vessel_id} onChange={(e) => setRhythmForm({ ...rhythmForm, vessel_id: e.target.value })}>
              <option value="">None (shore)</option>
              {patternLookups.vessels.map((vessel: any) => (
                <option key={vessel.vessel_id} value={vessel.vessel_id}>
                  {vessel.name} ({String(vessel.asset_type || "").replace(/^./, (char) => char.toUpperCase())})
                </option>
              ))}
            </select>
          </div>
          <div className="pfld">
            <label>Guide</label>
            <select value={rhythmForm.lead_guide_id} onChange={(e) => setRhythmForm({ ...rhythmForm, lead_guide_id: e.target.value })}>
              <option value="">TBD</option>
              {patternLookups.guides.map((guide: any) => (
                <option key={guide.leader_id} value={guide.leader_id}>{guide.name}</option>
              ))}
            </select>
          </div>
          <div className="pfld">
            <label>Cap</label>
            <input value={rhythmForm.default_capacity} onChange={(e) => setRhythmForm({ ...rhythmForm, default_capacity: e.target.value })} />
          </div>
          <div className="pfld">
            <label>Price</label>
            <select value={rhythmForm.price_per_diver} onChange={(e) => setRhythmForm({ ...rhythmForm, price_per_diver: e.target.value })}>
              <option value="45.000">45.00 JOD</option>
              <option value="40.000">40.00 JOD</option>
              <option value="35.000">35.00 JOD</option>
              <option value="30.000">30.00 JOD</option>
            </select>
          </div>
        </div>
        <div className="pfoot">
          <button className="p-save" type="button" onClick={handleCreateBlueCurrentPattern}>{isCreatingPattern ? "Saving…" : "Save"}</button>
          <button className="p-cancel" type="button" onClick={() => setEditingPatternId(null)}>Cancel</button>
          {editingPatternId && editingPatternId !== "__new__" ? (
            <button className="p-delete" type="button" onClick={handleDeletePattern}>Delete</button>
          ) : null}
        </div>
        <div className="pnote">Saved rhythm rows feed Apply rhythm. Course rhythm generation is parked for Courses.</div>
      </div>
    );
  }

  async function handleCreateDiveSession() {
    if (isCreating) return;

    const date = sessionForm.session_date;

    if (!date) {
      alert("Week not loaded yet.");
      return;
    }

    if (!sessionForm.dive_site_id) {
      alert("Choose a dive site before creating a session.");
      return;
    }

    setIsCreating(true);

    try {
      await createDashboardSession({
        dive_site_id: Number(sessionForm.dive_site_id),
        dive_datetime: `${date}T${sessionForm.start_time}:00`,
        meet_time: `${date}T${sessionForm.meet_time}:00`,
        meet_location: sessionForm.meet_location,
        session_type: sessionForm.session_type,
        dive_category: sessionForm.dive_category || null,
        vessel_id: sessionForm.vessel_id ? Number(sessionForm.vessel_id) : undefined,
        lead_guide_id: sessionForm.lead_guide_id ? Number(sessionForm.lead_guide_id) : null,
        price_per_diver: sessionForm.price_per_diver || null,
        notes: `Dashboard-created individual dive session ${new Date().toISOString()}`
      });

      setShowCreateSessionForm(false);
      refresh();
    } catch (err: any) {
      alert(err?.message || "Create failed");
    } finally {
      setIsCreating(false);
    }
  }

  function timeInputValue(value: any, fallback: string) {
    const raw = String(value || "");
    const tMatch = raw.match(/T(\d{2}:\d{2})/) || raw.match(/^(\d{2}:\d{2})/);
    return tMatch ? tMatch[1] : fallback;
  }

  function priceInputValue(session: any) {
    if (session.price_per_diver !== undefined && session.price_per_diver !== null) {
      return String(session.price_per_diver);
    }

    if (session.metadata?.price_per_diver !== undefined && session.metadata?.price_per_diver !== null) {
      return String(session.metadata.price_per_diver);
    }

    if (session.price_from_minor !== undefined && session.price_from_minor !== null) {
      const divisor = session.currency === "JOD" ? 1000 : 100;
      return (Number(session.price_from_minor) / divisor).toFixed(3);
    }

    return "45.000";
  }

  function openEditSession(session: any) {
    setEditSessionForm({
      session_date: session.session_date || String(session.starts_at || "").slice(0, 10) || currentLocalYmd(),
      start_time: timeInputValue(session.start_time || session.starts_at, "08:30"),
      meet_time: timeInputValue(session.meet_time, "08:00"),
      dive_site_id: session.dive_site_id ? String(session.dive_site_id) : "",
      session_type: session.session_type || session.metadata?.session_type || "shore",
      vessel_id: session.vessel_id ? String(session.vessel_id) : (session.metadata?.vessel_id ? String(session.metadata.vessel_id) : ""),
      lead_guide_id: session.lead_guide_id ? String(session.lead_guide_id) : (session.metadata?.lead_guide_id ? String(session.metadata.lead_guide_id) : ""),
      price_per_diver: priceInputValue(session)
    });
    setEditingSession(session);
  }

  async function handleUpdateDiveSession() {
    if (!editingSession || isUpdatingSession) return;

    if (!editSessionForm.dive_site_id) {
      alert("Choose a dive site before saving.");
      return;
    }

    setIsUpdatingSession(true);

    try {
      await updateDashboardSession(Number(editingSession.session_id), {
        dive_site_id: Number(editSessionForm.dive_site_id),
        dive_datetime: `${editSessionForm.session_date}T${editSessionForm.start_time}:00`,
        meet_time: `${editSessionForm.session_date}T${editSessionForm.meet_time}:00`,
        session_type: editSessionForm.session_type,
        vessel_id: editSessionForm.vessel_id ? Number(editSessionForm.vessel_id) : null,
        lead_guide_id: editSessionForm.lead_guide_id ? Number(editSessionForm.lead_guide_id) : null,
        price_per_diver: editSessionForm.price_per_diver || null
      });

      setEditingSession(null);
      refresh();
    } catch (err: any) {
      alert(err?.message || "Update session failed");
    } finally {
      setIsUpdatingSession(false);
    }
  }

  async function handleDelete(session_id: number) {
    if (deletingId !== null) return;

    const ok = window.confirm(`Delete session #${session_id}? This is only allowed when there are no booking dependencies.`);
    if (!ok) return;

    setDeletingId(session_id);

    try {
      await deleteDashboardSession(session_id);
      refresh();
    } catch (err: any) {
      alert(err?.message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  }

  async function handleCancel(session_id: number) {
    if (cancellingId !== null) return;

    const ok = window.confirm(`Cancel session #${session_id}?`);
    if (!ok) return;

    setCancellingId(session_id);

    try {
      await cancelDashboardSession(session_id);
      refresh();
    } catch (err: any) {
      alert(err?.message || "Cancel failed");
    } finally {
      setCancellingId(null);
    }
  }

  function openEditCourseRun(session: any) {
    const courseRunId = session.course_run_id || session.inventory_pointer?.course_run_id || session.metadata?.course_run_id;
    if (!courseRunId) return;

    setEditCourseRunForm({
      session_date: session.session_date || String(session.starts_at || "").slice(0, 10) || currentLocalYmd(),
      start_time: timeInputValue(session.start_time || session.starts_at, "09:00"),
      instructor_id: session.metadata?.instructor_id ? String(session.metadata.instructor_id) : "",
      location: session.metadata?.location || session.meet_location || "",
      max_capacity: session.capacity_total ? String(session.capacity_total) : "8"
    });
    setEditingCourseRun(session);
  }

  async function handleUpdateCourseRun() {
    if (!editingCourseRun || isUpdatingCourseRun) return;

    const operatorId = scheduleData?.operator_id;
    const courseRunId = editingCourseRun.course_run_id || editingCourseRun.inventory_pointer?.course_run_id || editingCourseRun.metadata?.course_run_id;

    if (!operatorId || !courseRunId) {
      alert("Course run is missing operator or run id.");
      return;
    }

    setIsUpdatingCourseRun(true);

    try {
      await updateDashboardCourseRun(operatorId, courseRunId, {
        start_at: `${editCourseRunForm.session_date}T${editCourseRunForm.start_time}:00`,
        end_at: `${editCourseRunForm.session_date}T${editCourseRunForm.start_time}:00`,
        instructor_id: editCourseRunForm.instructor_id ? Number(editCourseRunForm.instructor_id) : null,
        location: editCourseRunForm.location || null,
        max_capacity: Number(editCourseRunForm.max_capacity)
      });

      setEditingCourseRun(null);
      refresh();
    } catch (err: any) {
      alert(err?.message || "Update course run failed");
    } finally {
      setIsUpdatingCourseRun(false);
    }
  }

  function openCancelCourseRun(session: any) {
    const courseRunId = session.course_run_id || session.inventory_pointer?.course_run_id || session.metadata?.course_run_id;
    if (!courseRunId) return;

    setCourseCancelError(null);
    setPendingCourseCancel(session);
  }

  async function confirmCancelCourseRun() {
    if (!pendingCourseCancel || isCancellingCourseRun) return;

    const operatorId = scheduleData?.operator_id;
    const courseRunId = pendingCourseCancel.course_run_id || pendingCourseCancel.inventory_pointer?.course_run_id || pendingCourseCancel.metadata?.course_run_id;

    if (!operatorId || !courseRunId) {
      setCourseCancelError("Course run is missing operator or run id.");
      return;
    }

    setIsCancellingCourseRun(true);
    setCourseCancelError(null);

    try {
      await cancelDashboardCourseRun(operatorId, courseRunId);
      setPendingCourseCancel(null);
      refresh();
    } catch (err: any) {
      setCourseCancelError(err?.message || "Cancel course run failed");
    } finally {
      setIsCancellingCourseRun(false);
    }
  }

  if (state.status === "loading") {
    return (
      <div style={styles.page}>
        <h1 style={styles.h1}>Schedule</h1>
        <div style={styles.card}>Loading schedule…</div>
      </div>
    );
  }

  if (state.status === "error" && !scheduleData) {
    return (
      <div style={styles.page}>
        <h1 style={styles.h1}>Schedule</h1>
        <ErrorBanner error={(state as any).error} />
      </div>
    );
  }

  const { dates, map } = grouped;
  const isAtFloor = Boolean(weekFloor && weekStart && weekStart <= weekFloor);

  return (
    <div className="schedule-v3">
      <div className="crown">
        <div>
          <h1>Schedule</h1>
          <p className="sub">Your operating week — fill the board fast, or lay down the rhythm that feeds your website and app.</p>
        </div>
        <div className="wid">Your weekly rhythm is what divers<br />see on <b>your widget</b> &amp; <b>the app</b>.</div>
      </div>

      {state.status === "error" ? <ErrorBanner error={(state as any).error} /> : null}

      <div className="modes">
        <button
          type="button"
          className={scheduleView === "schedule" ? "on" : ""}
          onClick={() => setScheduleView("schedule")}
        >
          <svg className="mi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4.5" width="18" height="16" rx="2"/><path d="M3 9h18M8 3v3M16 3v3"/></svg>
          This week
        </button>

        <button
          type="button"
          className={scheduleView === "template" ? "on" : ""}
          onClick={() => setScheduleView("template")}
        >
          <svg className="mi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/><path d="M3 21v-5h5"/></svg>
          Weekly rhythm
        </button>
      </div>

      <p className="mhint">
        {scheduleView === "schedule"
          ? "The real sessions running this week. Add a one-off, or apply your rhythm to fill the week."
          : "Your average week — the rhythm that feeds your widget and app. Apply it to lay down a full week at once."}
      </p>

      <div className={applyDialog.open ? "apply-scrim open" : "apply-scrim"} onClick={() => setApplyDialog({ open: false, result: null })}></div>
      <div className={applyDialog.open ? "apply-card open" : "apply-card"}>
        <div className="ach">
          <div className="aicon">↻</div>
          <div>
            <h3>Apply rhythm</h3>
            <p>Week of {applyDialog.start || applyTargetWeek || weekStart || "current week"}</p>
          </div>
        </div>
        <div className="ab">
          {applyDialog.result ? (
            <>
              <div className="apply-line add"><span className="an">{Number(applyDialog.result.created_count || 0)}</span><span className="al">sessions added</span></div>
              <div className="apply-line skip"><span className="an">{Number(applyDialog.result.skipped_count || 0)}</span><span className="al">already existed / skipped</span></div>
              <div className="apply-note">This summary consumes backend generate truth. Applying twice should skip existing sessions.</div>
            </>
          ) : (
            <>
              <div className="apply-line keep"><span className="an">{patterns.length}</span><span className="al">rhythm rows ready to apply</span></div>
              <div className="apply-note">Confirm to generate real sessions for the selected week. Duplicate protection is enforced by the backend.</div>
            </>
          )}
        </div>
        <div className="apply-foot">
          {applyDialog.result ? (
            <button className="btn-confirm" type="button" onClick={() => setApplyDialog({ open: false, result: null })}>Done</button>
          ) : (
            <button className="btn-confirm" type="button" onClick={confirmApplyRhythm} disabled={isGeneratingFromPatterns}>{isGeneratingFromPatterns ? "Applying…" : "Confirm apply"}</button>
          )}
          <button className="btn-back" type="button" onClick={() => setApplyDialog({ open: false, result: null })}>Cancel</button>
        </div>
      </div>

      {scheduleView === "schedule" ? (
        <div className="weekbar">
          <div className="nav">
            {weekStart && (
            <>
              <button
                type="button"
                className="wbtn"
                onClick={() => {
                  if (!weekStart) return;

                  const d = new Date(weekStart);
                  d.setDate(d.getDate() - 7);
                  const candidate = d.toISOString().split("T")[0];

                  if (weekFloor && candidate < weekFloor) {
                    setWeekStart(weekFloor);
                  } else {
                    setWeekStart(candidate);
                  }
                }}
                disabled={isAtFloor}
              >
                ‹ Prev
              </button>

              <button
                type="button"
                className="wbtn"
                onClick={() => {
                  if (!weekStart) return;

                  const d = new Date(weekStart);
                  d.setDate(d.getDate() + 7);
                  setWeekStart(d.toISOString().split("T")[0]);
                }}
              >
                Next ›
              </button>
            </>
          )}
        </div>

        <div>
          <div className="wk">Week of {scheduleData?.week?.start || weekStart || "Loading"} – {scheduleData?.week?.end || "Loading"}</div>
          <div className="wkmeta">Operator #{scheduleData?.operator_id || "—"} · {(scheduleData?.sessions || []).length} sessions · {new Set((scheduleData?.sessions || []).map((session: any) => session.vessel_name || session.metadata?.vessel_name).filter(Boolean)).size} vessels</div>
        </div>

        <div className="spacer"></div>

        {scheduleView === "schedule" ? (
          <button
            type="button"
            className="btn-create"
            onClick={() => setShowCreateSessionForm((value) => !value)}
            disabled={isCreating}
          >
            ＋ Create dive session
          </button>
        ) : null}

        {state.status === "ready" && state.isRefreshing ? (
          <div className="wkmeta">Refreshing…</div>
        ) : null}
        </div>
      ) : null}

      {scheduleView === "schedule" && showCreateSessionForm ? (
        <div className="createcard">
          <div className="ch">
            <h3>Create a dive session</h3>
            <p>A one-off dive for this week. Multiple sessions can share a time slot — two boats and shore dives at 09:00 is normal.</p>
          </div>

          <div className="cb">
          <div className="fgrid">
            <label className="fld">
              <span>Date</span>
              <input
                type="date"
                value={sessionForm.session_date}
                min={currentLocalYmd()}
                onChange={(e) => setSessionForm({ ...sessionForm, session_date: e.target.value })}
              />
            </label>

            <label className="fld">
              <span>Start time</span>
              <select
                value={sessionForm.start_time}
                onChange={(e) => {
                  const nextStartTime = e.target.value;
                  setSessionForm({
                    ...sessionForm,
                    start_time: nextStartTime,
                    meet_time: subtractThirtyMinutes(nextStartTime)
                  });
                }}
              >
                {TEMPLATE_TIME_OPTIONS.map((time) => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </label>

            <label className="fld">
              <span>Meet time</span>
              <select
                value={sessionForm.meet_time}
                onChange={(e) => setSessionForm({ ...sessionForm, meet_time: e.target.value })}
              >
                {TEMPLATE_TIME_OPTIONS.map((time) => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </label>

            <label className="fld">
              <span>Meet location</span>
              <select
                value={sessionForm.meet_location}
                onChange={(e) => setSessionForm({ ...sessionForm, meet_location: e.target.value })}
              >
                <option value="">Choose meet location</option>

                {(patternLookups.meet_locations || []).map((location: any) => (
                  <option
                    key={location.meet_location_id || location.label}
                    value={location.label}
                  >
                    {location.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="fld">
              <span>Dive site</span>
              <select
                value={sessionForm.dive_site_id}
                onChange={(e) => setSessionForm({ ...sessionForm, dive_site_id: e.target.value })}
              >
                <option value="">Choose dive site</option>
                {patternLookups.dive_sites.map((site: any) => (
                  <option key={site.dive_site_id} value={site.dive_site_id}>{site.name}</option>
                ))}
              </select>
            </label>

            <label className="fld">
              <span>Entry type</span>
              <select
                value={sessionForm.session_type}
                onChange={(e) => setSessionForm({ ...sessionForm, session_type: e.target.value })}
              >
                <option value="shore">Shore dive</option>
                <option value="boat">Boat dive</option>
              </select>
            </label>

            <label className="fld">
              <span>Dive category</span>
              <select
                value={sessionForm.dive_category}
                onChange={(e) => setSessionForm({ ...sessionForm, dive_category: e.target.value })}
              >
                <option value="">Choose dive category</option>
                {(patternLookups.dive_categories || []).map((category: any) => (
                  <option key={category.operator_dive_category_id || category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="fld">
              <span>Vessel / vehicle</span>
              <select
                value={sessionForm.vessel_id}
                onChange={(e) => setSessionForm({ ...sessionForm, vessel_id: e.target.value })}
              >
                <option value="">No vehicle / vessel</option>
                {patternLookups.vessels.map((vessel: any) => (
                  <option key={vessel.vessel_id} value={vessel.vessel_id}>
                    {vessel.name} ({String(vessel.asset_type || "").replace(/^./, (char) => char.toUpperCase())})
                  </option>
                ))}
              </select>
            </label>

            <label className="fld">
              <span>Staff / guide</span>

              <select
                value={sessionForm.lead_guide_id}
                onChange={(e) =>
                  setSessionForm({
                    ...sessionForm,
                    lead_guide_id: e.target.value
                  })
                }
              >
                <option value="">TBD</option>

                {patternLookups.guides.map((guide: any) => (
                  <option
                    key={guide.leader_id}
                    value={guide.leader_id}
                  >
                    {guide.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="fld">
              <span>Price / diver (JOD)</span>
              <select
                value={sessionForm.price_per_diver}
                onChange={(e) => setSessionForm({ ...sessionForm, price_per_diver: e.target.value })}
              >
                <option value="45.000">45.00 JOD</option>
                <option value="40.000">40.00 JOD</option>
                <option value="35.000">35.00 JOD</option>
                <option value="30.000">30.00 JOD</option>
                <option value="25.000">25.00 JOD</option>
              </select>
            </label>

          </div>

          <div className="cfoot">
            <button
              onClick={handleCreateDiveSession}
              disabled={isCreating}
              className="btn-primary"
            >
              {isCreating ? "Creating session..." : "Create session"}
            </button>

            <button
              onClick={() => setShowCreateSessionForm(false)}
              type="button"
              className="btn-ghost"
            >
              Cancel
            </button>
            <span className="hint">Adds to selected day · joins matching time slot</span>
          </div>
          </div>
        </div>
      ) : null}

      {editingSession ? (
        <div className="schedule-modal-layer" role="presentation" onClick={() => setEditingSession(null)}>
          <div className="schedule-modal-panel" role="dialog" aria-modal="true" aria-label="Edit dive session" onClick={(event) => event.stopPropagation()}>
            <div className="createcard editcard">
              <div className="ch">
                <h3>Edit dive session</h3>
                <p>Update the operational details for this scheduled dive. Booked sessions may reject core changes.</p>
              </div>

              <div className="cb">
                <div className="fgrid">
                  <label className="fld">
                    <span>Date</span>
                    <input
                      type="date"
                      value={editSessionForm.session_date}
                      onChange={(e) => setEditSessionForm({ ...editSessionForm, session_date: e.target.value })}
                    />
                  </label>

                  <label className="fld">
                    <span>Start time</span>
                    <select
                      value={editSessionForm.start_time}
                      onChange={(e) =>
                        setEditSessionForm({
                          ...editSessionForm,
                          start_time: e.target.value,
                          meet_time: subtractThirtyMinutes(e.target.value)
                        })
                      }
                    >
                      {TEMPLATE_TIME_OPTIONS.map((time) => <option key={time} value={time}>{time}</option>)}
                    </select>
                  </label>

                  <label className="fld">
                    <span>Meet time</span>
                    <select
                      value={editSessionForm.meet_time}
                      onChange={(e) => setEditSessionForm({ ...editSessionForm, meet_time: e.target.value })}
                    >
                      {TEMPLATE_TIME_OPTIONS.map((time) => <option key={time} value={time}>{time}</option>)}
                    </select>
                  </label>

                  <label className="fld">
                    <span>Entry type</span>
                    <select
                      value={editSessionForm.session_type}
                      onChange={(e) => setEditSessionForm({ ...editSessionForm, session_type: e.target.value })}
                    >
                      <option value="shore">Shore dive</option>
                      <option value="boat">Boat dive</option>
                    </select>
                  </label>

                  <label className="fld">
                    <span>Dive site</span>
                    <select
                      value={editSessionForm.dive_site_id}
                      onChange={(e) => setEditSessionForm({ ...editSessionForm, dive_site_id: e.target.value })}
                    >
                      <option value="">Choose dive site</option>
                      {patternLookups.dive_sites.map((site: any) => (
                        <option key={site.dive_site_id} value={site.dive_site_id}>{site.name}</option>
                      ))}
                    </select>
                  </label>

                  <label className="fld">
                    <span>Vessel / vehicle</span>
                    <select
                      value={editSessionForm.vessel_id}
                      onChange={(e) => setEditSessionForm({ ...editSessionForm, vessel_id: e.target.value })}
                    >
                      <option value="">No vehicle / vessel</option>
                      {patternLookups.vessels.map((vessel: any) => (
                        <option key={vessel.vessel_id} value={vessel.vessel_id}>
                          {vessel.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="fld">
                    <span>Staff / guide</span>
                    <select
                      value={editSessionForm.lead_guide_id}
                      onChange={(e) => setEditSessionForm({ ...editSessionForm, lead_guide_id: e.target.value })}
                    >
                      <option value="">TBD</option>
                      {patternLookups.guides.map((guide: any) => (
                        <option key={guide.leader_id} value={guide.leader_id}>{guide.name}</option>
                      ))}
                    </select>
                  </label>

                  <label className="fld">
                    <span>Price / diver (JOD)</span>
                    <select
                      value={editSessionForm.price_per_diver}
                      onChange={(e) => setEditSessionForm({ ...editSessionForm, price_per_diver: e.target.value })}
                    >
                      <option value="45.000">45.00 JOD</option>
                      <option value="40.000">40.00 JOD</option>
                      <option value="35.000">35.00 JOD</option>
                      <option value="30.000">30.00 JOD</option>
                      <option value="25.000">25.00 JOD</option>
                    </select>
                  </label>
                </div>

                <div className="cfoot">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleUpdateDiveSession}
                    disabled={isUpdatingSession}
                  >
                    {isUpdatingSession ? "Saving..." : "Save session"}
                  </button>

                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setEditingSession(null)}
                  >
                    Cancel
                  </button>

                  <span className="hint">Updates this scheduled dive only</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {editingCourseRun ? (
        <div className="schedule-modal-layer" role="presentation" onClick={() => setEditingCourseRun(null)}>
          <div className="schedule-modal-panel course-run-modal course-run-modal--edit" role="dialog" aria-modal="true" aria-label="Edit course run" onClick={(event) => event.stopPropagation()}>
            <div className="createcard editcard">
              <div className="ch">
                <h3>Edit course run</h3>
                <p>Update this scheduled class without leaving the schedule.</p>
              </div>

              <div className="cb">
                <div className="fgrid">
                  <label className="fld">
                    <span>Date</span>
                    <input
                      type="date"
                      value={editCourseRunForm.session_date}
                      onChange={(e) => setEditCourseRunForm({ ...editCourseRunForm, session_date: e.target.value })}
                    />
                  </label>

                  <label className="fld">
                    <span>Start time</span>
                    <select
                      value={editCourseRunForm.start_time}
                      onChange={(e) => setEditCourseRunForm({ ...editCourseRunForm, start_time: e.target.value })}
                    >
                      {TEMPLATE_TIME_OPTIONS.map((time) => <option key={time} value={time}>{time}</option>)}
                    </select>
                  </label>

                  <label className="fld">
                    <span>Instructor</span>
                    <select
                      value={editCourseRunForm.instructor_id}
                      onChange={(e) => setEditCourseRunForm({ ...editCourseRunForm, instructor_id: e.target.value })}
                    >
                      <option value="">TBD</option>
                      {patternLookups.guides.map((guide: any) => (
                        <option key={guide.leader_id} value={guide.leader_id}>{guide.name}</option>
                      ))}
                    </select>
                  </label>

                  <label className="fld">
                    <span>Location</span>
                    <input
                      value={editCourseRunForm.location}
                      onChange={(e) => setEditCourseRunForm({ ...editCourseRunForm, location: e.target.value })}
                      placeholder="e.g. Dive Center Pool"
                    />
                  </label>

                  <label className="fld">
                    <span>Capacity</span>
                    <input
                      type="number"
                      min="1"
                      value={editCourseRunForm.max_capacity}
                      onChange={(e) => setEditCourseRunForm({ ...editCourseRunForm, max_capacity: e.target.value })}
                    />
                  </label>
                </div>

                <div className="cfoot">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleUpdateCourseRun}
                    disabled={isUpdatingCourseRun}
                  >
                    {isUpdatingCourseRun ? "Saving..." : "Save course run"}
                  </button>

                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setEditingCourseRun(null)}
                  >
                    Cancel
                  </button>

                  <span className="hint">Updates this scheduled course only</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {pendingCourseCancel ? (
        <div className="schedule-modal-layer" role="presentation" onClick={() => setPendingCourseCancel(null)}>
          <div className="schedule-modal-panel course-run-modal course-run-modal--cancel" role="dialog" aria-modal="true" aria-label="Cancel course run" onClick={(event) => event.stopPropagation()}>
            <div className="createcard editcard">
              <div className="ch">
                <h3>Cancel course run</h3>
                <p>This removes the class from the schedule. Runs with enrolled students must be cleared first.</p>
              </div>

              <div className="cb">
                <p className="note"><b>{pendingCourseCancel.title || pendingCourseCancel.metadata?.course_title || "Course run"}</b></p>
                {courseCancelError ? <p className="note">{courseCancelError}</p> : null}

                <div className="cfoot">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={confirmCancelCourseRun}
                    disabled={isCancellingCourseRun}
                  >
                    {isCancellingCourseRun ? "Cancelling..." : "Cancel course run"}
                  </button>

                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setPendingCourseCancel(null)}
                  >
                    Keep class
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {scheduleView === "template" ? (
        <div>
          <div className="rhythm-intro">
            <div className="flow">
              <span className="node">Your weekly rhythm</span>
              <span className="arr">→</span>
              <span className="node">generates sessions</span>
              <span className="arr">→</span>
              <span className="node">widget &amp; app</span>
              <span className="arr">→</span>
              <span className="node">divers book</span>
            </div>
            <div className="txt">This is your <b>average week</b> — the rhythm divers see on your website and app. Apply it to any week to lay the whole schedule down at once, then adjust the real week as life happens.</div>
          </div>

          <div className="rhythm-toolbar">
            <div>
              <div className="wk" style={{ fontSize: 15, fontWeight: 800, color: "var(--violet)" }}>Weekly rhythm</div>
              <div className="meta">
                {patternsLoading
                  ? "Loading templates…"
                  : `${patterns.length} template rows · ${patternLookups.dive_sites.length} sites · ${patternLookups.vessels.length} vessels · ${patternLookups.guides.length} guides · the blueprint, not a real week`}
              </div>
            </div>
            <div className="spacer"></div>
            <button className="btn-addrow" type="button" onClick={() => openRhythmAdd(1)} disabled={isCreatingPattern}>
              {isCreatingPattern ? "Adding…" : "＋ Add to rhythm"}
            </button>
            <div className="apply-group">
              <div className="apply-target">
                <span className="at-label">Apply to</span>
                <select className="at-select" aria-label="Apply target" value={applyTargetWeek || weekStart || scheduleData?.week?.start || ""} onChange={(e) => setApplyTargetWeek(e.target.value)}>
                  {Array.from({ length: 52 }, (_, index) => {
                    const base = weekStart || scheduleData?.week?.start || currentLocalYmd();
                    const value = addDaysYmd(base, index * 7);
                    return <option key={value} value={value}>Week of {value}</option>;
                  })}
                </select>
              </div>
              <button className="btn-apply" type="button" onClick={openApplyDialog} disabled={isGeneratingFromPatterns}>
                <svg className="ai" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/><path d="M3 21v-5h5"/></svg>
                {isGeneratingFromPatterns ? "Applying…" : "Apply rhythm"}
              </button>
            </div>
          </div>

          <div className="apply-explain">"Add to rhythm" edits this blueprint. <b>Apply</b> stamps it onto the week(s) you pick — generating real sessions. Applying twice can't double-book; you'll get a summary of what was added.</div>

          <div className="rweek">
            {RHYTHM_DAYS.map((day) => {
              const groupedPatterns = groupPatternsByWeekday(patterns);
              const dayPatterns = groupedPatterns[day.value] || [];

              return (
                <div className="rrow" key={day.value}>
                  <div className="rday">{day.label}</div>
                  <div className="rcells">
                    {dayPatterns.map((pattern) => (
                      <div className={editingPatternId === String(pattern.pattern_id) ? `${rhythmChipClass(pattern)} editing ${Number(pattern.weekday) >= 5 ? "flip-up" : ""}` : rhythmChipClass(pattern)} key={pattern.pattern_id} onClick={() => openRhythmEdit(pattern)}>
                        <span className="tt">{shortTime(pattern.start_time)}</span>
                        <span className="ts">{pattern.dive_site_name || `Site #${pattern.dive_site_id}`}</span>
                        <span className="tm">{rhythmEntryLabel(pattern)} · cap {pattern.default_capacity || "default"} · {rhythmPatternPrice(pattern)}</span>
                        {editingPatternId === String(pattern.pattern_id) ? renderRhythmEditor() : null}
                      </div>
                    ))}

                    <button className="tchip-add" type="button" onClick={() => openRhythmAdd(day.value)} disabled={isCreatingPattern}>
                      {dayPatterns.length === 0 ? `＋ Add ${day.label} rhythm` : "＋"}
                    </button>
                    {editingPatternId === "__new__" && rhythmForm.weekday === String(day.value) ? (
                      <div className={Number(rhythmForm.weekday) >= 5 ? "tchip editing flip-up" : "tchip editing"}>
                        {renderRhythmEditor()}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="legend">
            <span><i style={{ background: "var(--teal)" }}></i> Shore dive</span>
            <span><i style={{ background: "var(--deep-aqua)" }}></i> Boat dive</span>
            <span><i style={{ background: "var(--violet)" }}></i> Course run</span>
          </div>

          <p className="note"><b>Rhythm → reality, never a clash.</b> The rhythm is your <i>intent</i>; applying it generates real sessions for the week (with duplicate protection so applying twice can't double-book a slot). Once generated, the week is authoritative — edit, cancel, or add a one-off boat without touching the rhythm. Because capacity is per session, a generated 09:00 and a hand-added 09:00 boat simply coexist in the same slot. The rhythm is also the contract with your public widget: change the rhythm, and what divers can book on your site and app changes with it.</p>
        </div>
      ) : null}

      {scheduleView === "schedule" ? (
        <div id="weekmode">
          {currentDates.length === 0 ? (
            <div className="day">
              <div className="day-h collapsed">
                <span className="caret">▶</span>
                <span className="dname">No sessions scheduled</span>
                <span className="spacer"></span>
                <span className="dcount">— no sessions</span>
                <span className="dfill">apply rhythm to fill</span>
              </div>
            </div>
          ) : null}

          {visibleDates.length > 0 ? (
            visibleDates.map((date, index) => {
              const daySessions = map[date];
              const collapsed = Boolean(collapsedDays[date]);
              const boats = vesselCount(daySessions);
              const slots = groupBySlot(daySessions);
              const isPastDate = date < todayYmd;
              const showPastDivider = isPastDate && (index === 0 || visibleDates[index - 1] >= todayYmd);

              return (
                <div className="schedule-date-group" key={`date-group-${date}`}>
                  {showPastDivider ? (
                    <div className="past-days-divider">
                      <span></span>
                      <b>Earlier this week</b>
                      <span></span>
                    </div>
                  ) : null}
                  <div className="day">
                  <div
                    className={collapsed ? "day-h collapsed" : "day-h"}
                    onClick={() =>
                      setCollapsedDays((prev) => ({
                        ...prev,
                        [date]: !prev[date]
                      }))
                    }
                  >
                    <span className="caret" style={!collapsed ? { transform: "rotate(90deg)" } : undefined}>▶</span>
                    <span className="dname">{weekdayFromYmd(date)}</span>
                    <span className="ddate">{monthDay(date)}</span>
                    <span className="spacer"></span>
                    <span className="dcount">
                      {daySessions.length === 0
                        ? "— no sessions"
                        : `${daySessions.length} sessions${boats > 0 && !collapsed ? ` · ${boats} boats` : ""}`}
                    </span>
                    <span className="dfill">{dayFill(daySessions)}</span>
                  </div>

                  {!collapsed ? (
                    <div className="day-body">
                      {slots.map((slot) => (
                        <div className="slot" key={`${date}-${slot.time}`}>
                          <div className="slot-h">
                            <span className="slot-time">{slot.time}</span>
                            <span className="slot-meet">meet {shortTime((slot.sessions[0] as any).meet_time)}</span>
                            <span className="slot-rule"></span>
                            <span className="slot-tag"><b>{slot.sessions.length}</b> running{slot.sessions.length > 1 ? " this slot" : ""}</span>
                          </div>

                          {slot.sessions.map((s: any) => {
                            const rowKey = s.unified_id || s.id || s.session_id || s.course_run_id;
                            const isDiveSession = Boolean(s.session_id);
                            const used = capacityUsed(s);
                            const total = capacityTotal(s);
                            const guide = guideLabel(s);
                            const [priceAmount, priceCurrency] = priceParts(s.price_from_minor, s.currency);

                            return (
                              <div className={sessionClassName(s)} key={rowKey}>
                                <div className="spine"></div>
                                <div className="body">
                                  <div className="top">
                                    <span className="site">{s.title || s.site_name}</span>
                                    <span className={s.type === "course" ? "cat course" : s.session_type === "boat" ? "cat boat" : "cat dive"}>{sessionKindLabel(s)}</span>
                                    <span className="cat entry">{entryLabel(s)}</span>
                                  </div>
                                  <div className="meta">
                                    <span className="vessel">{vesselLabel(s).startsWith("No ") ? vesselLabel(s) : <>Vessel <b>{vesselLabel(s)}</b></>}</span>
                                    <span className="sep">·</span>
                                    <span>{s.type === "course" ? "Instructor" : "Guide"} {guide ? <span>{guide}</span> : <span className="staff-tbd">TBD</span>}</span>
                                    <span className="sep">·</span>
                                    <span className="mono">{meetLocationLabel(s)}</span>
                                  </div>
                                </div>
                                <div className="right">
                                  <div className="cap">
                                    <div className="nums">{used} <span className="of">/ {total || "—"}</span></div>
                                    <div className="bar"><i className={capClass(s)} style={{ width: `${capPercent(s)}%` }}></i></div>
                                    <div className="price">{priceAmount} <span className="cur">{priceCurrency}</span></div>
                                  </div>
                                  <div className="acts">
                                    {isDiveSession ? (
                                      <>
                                        <button onClick={() => openEditSession(s)}>Edit</button>
                                        {Boolean((s as any).is_deletable) ? (
                                          <button className="danger" onClick={() => handleDelete(Number(s.session_id))} disabled={deletingId === Number(s.session_id)}>
                                            {deletingId === Number(s.session_id) ? "Deleting…" : "Delete"}
                                          </button>
                                        ) : (
                                          <button className="danger" onClick={() => handleCancel(Number(s.session_id))} disabled={cancellingId === Number(s.session_id)}>
                                            {cancellingId === Number(s.session_id) ? "Cancelling…" : "Cancel"}
                                          </button>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        <button onClick={() => openEditCourseRun(s)}>Edit</button>
                                        <button className="danger" onClick={() => openCancelCourseRun(s)}>Cancel</button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  ) : null}
                  </div>
                </div>
              );
            })
          ) : null}

          <p className="note"><b>Why time-slot grouping:</b> on the dock, an operator thinks "what's running at 9?" — not in a flat list. A big group walks in, you drop a second boat into the same slot; multiple sessions sharing a time is the norm, not a conflict. Capacity is per session, so the slot just fills. Boat dives carry the deep-aqua spine, shore dives teal, courses violet — readable at a glance from across the shop.</p>
        </div>
      ) : null}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: "24px 28px 48px",
    maxWidth: 1100,
    margin: "0 auto"
  },
  h1: {
    fontSize: 22,
    margin: "8px 0 12px"
  },
  h2: {
    fontSize: 18,
    margin: "0 0 4px"
  },
  scheduleTabs: {
    display: "flex",
    gap: 8,
    marginBottom: 12
  },
  scheduleTab: {
    padding: "9px 14px",
    borderRadius: 999,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "var(--surface)",
    color: "var(--ink)",
    fontWeight: 800,
    cursor: "pointer"
  },
  scheduleTabActive: {
    padding: "9px 14px",
    borderRadius: 999,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "var(--deep-aqua)",
    color: "var(--surface)",
    fontWeight: 800,
    cursor: "pointer"
  },
  createSessionPanel: {
    padding: 14,
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 12,
    background: "var(--surface)",
    marginBottom: 14,
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
  },
  templatePanel: {
    padding: 14,
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 12,
    background: "rgba(0, 212, 255, 0.04)",
    marginBottom: 14
  },
  templatePanelHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    marginBottom: 12
  },
  templateHelp: {
    fontSize: 13,
    opacity: 0.72
  },
  templateEmpty: {
    padding: 12,
    borderRadius: 10,
    background: "var(--surface)",
    border: "1px dashed rgba(0,0,0,0.18)",
    fontSize: 13,
    opacity: 0.8
  },
  templateForm: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 8,
    marginBottom: 12
  },
  templateField: {
    display: "grid",
    gap: 4,
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    opacity: 0.85
  },
  templateActions: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    marginBottom: 12
  },
  templatePrimaryButton: {
    padding: "10px 14px",
    borderRadius: 10,
    cursor: "pointer",
    border: "1px solid rgba(0,0,0,0.10)",
    background: "var(--deep-aqua)",
    color: "var(--surface)",
    fontWeight: 700
  },
  templateSecondaryButton: {
    padding: "10px 14px",
    borderRadius: 10,
    cursor: "pointer",
    border: "1px solid rgba(0,0,0,0.10)",
    background: "var(--teal)",
    color: "var(--surface)",
    fontWeight: 700
  },
  templateRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    padding: 12,
    borderRadius: 10,
    background: "var(--surface)",
    border: "1px solid rgba(0,0,0,0.08)"
  },
  templateTitle: {
    fontSize: 15,
    fontWeight: 800
  },
  templateSubline: {
    fontSize: 13,
    opacity: 0.72,
    marginTop: 3
  },
  templateMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "flex-end",
    fontSize: 12,
    fontWeight: 700,
    opacity: 0.78
  },
  meta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    padding: 12,
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 10,
    marginBottom: 14
  },
  card: {
    padding: 14,
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 10
  },
  dayBlock: {
    marginBottom: 14,
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 10,
    overflow: "hidden"
  },
  dayHeaderButton: {
    width: "100%",
    padding: "14px 16px",
    background: "rgba(0,0,0,0.03)",
    fontWeight: 800,
    fontSize: 18,
    border: "none",
    borderBottom: "1px solid rgba(0,0,0,0.05)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    textAlign: "left"
  },

  dayHeaderMeta: {
    fontSize: 12,
    opacity: 0.7,
    fontWeight: 700
  },
  dayList: {
    padding: 10,
    display: "grid",
    gap: 16
  },

  editButton: {
    padding: "7px 12px",
    borderRadius: 10,
    cursor: "pointer",
    border: "1px solid rgba(15, 23, 42, 0.22)",
    background: "var(--surface)",
    color: "var(--ink)",
    fontWeight: 800,
    fontSize: 12
  },
  cancelButton: {
    padding: "7px 12px",
    borderRadius: 10,
    cursor: "pointer",
    border: "1px solid rgba(15, 23, 42, 0.22)",
    background: "var(--surface)",
    color: "var(--ink)",
    fontWeight: 800,
    fontSize: 12
  },
  deleteButton: {
    padding: "7px 12px",
    borderRadius: 10,
    cursor: "pointer",
    border: "1px solid rgba(15, 23, 42, 0.22)",
    background: "var(--surface)",
    color: "var(--ink)",
    fontWeight: 800,
    fontSize: 12
  },

  courseInventoryLabel: {
    marginTop: 10,
    fontSize: 12,
    opacity: 0.7
  },
  bannerWarn: {
    padding: 12,
    borderRadius: 10,
    border: "1px solid rgba(255,200,0,0.45)",
    background: "rgba(255,200,0,0.12)",
    marginBottom: 12
  },
  bannerError: {
    padding: 12,
    borderRadius: 10,
    border: "1px solid rgba(255,0,0,0.45)",
    background: "rgba(255,0,0,0.12)",
    marginBottom: 12
  }
};